import { jsPDF } from 'jspdf'
import { company, CONTACT_EMAIL } from '../data/site'
import { formatDate } from './format'
import { calculateTotals, lineSubtotal, lineTotal } from './quote'
import type { AdminSettings } from '../types/admin'
import type { Quote } from '../types/quote'

/** Filnavn: LYKKEVAERK-Tilbud-TIL-2026-0007.pdf */
export function quoteFileName(quoteNumber: string): string {
  return `LYKKEVAERK-Tilbud-${quoteNumber || 'kladde'}.pdf`
}

const INK: [number, number, number] = [22, 35, 47]
const MUTED: [number, number, number] = [107, 122, 136]
const LINE: [number, number, number] = [222, 218, 210]
const ACCENT: [number, number, number] = [200, 150, 62]

const MARGIN = 18
const PAGE_W = 210
const PAGE_H = 297
const RIGHT = PAGE_W - MARGIN

function kr(amount: number): string {
  const value = Number.isFinite(amount) ? amount : 0
  return `${value.toLocaleString('da-DK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} kr.`
}

/** Henter logoet som data-URL, så det kan lægges ind i PDF'en. */
async function loadLogo(): Promise<string | null> {
  try {
    const response = await fetch(company.logoWide, { mode: 'cors' })
    if (!response.ok) return null
    const blob = await response.blob()
    return await new Promise<string | null>((resolve) => {
      const reader = new FileReader()
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null)
      reader.onerror = () => resolve(null)
      reader.readAsDataURL(blob)
    })
  } catch {
    // Uden logo bruges et tekst-wordmark i stedet — PDF'en fejler aldrig af den grund.
    return null
  }
}

/**
 * Bygger tilbuddet som en ren, printvenlig A4-PDF i LYKKEVÆRKs identitet.
 * Bruger de gemte tilbudsdata, så forhåndsvisning og download er identiske.
 */
export async function buildQuotePdf(quote: Quote, settings: AdminSettings): Promise<jsPDF> {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' })
  doc.setFont('helvetica', 'normal')

  const logo = await loadLogo()
  let y = MARGIN

  /* Hoved: logo til venstre, afsender til højre */
  if (logo) {
    try {
      const props = doc.getImageProperties(logo)
      const height = 13
      const width = (props.width / props.height) * height
      doc.addImage(logo, 'PNG', MARGIN, y, width, height)
    } catch {
      doc.setFont('helvetica', 'bold').setFontSize(20).setTextColor(...INK)
      doc.text(settings.companyName || 'LYKKEVÆRK', MARGIN, y + 9)
    }
  } else {
    doc.setFont('helvetica', 'bold').setFontSize(20).setTextColor(...INK)
    doc.text(settings.companyName || 'LYKKEVÆRK', MARGIN, y + 9)
  }

  doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(...MUTED)
  const senderLines = [
    settings.companyName || 'LYKKEVÆRK',
    `CVR: ${settings.cvr}`,
    settings.phone,
    CONTACT_EMAIL,
  ].filter(Boolean)
  senderLines.forEach((line, index) => {
    doc.text(line, RIGHT, y + 3 + index * 4.2, { align: 'right' })
  })

  y += 24

  /* Titel og datoer */
  doc.setFont('helvetica', 'bold').setFontSize(24).setTextColor(...INK)
  doc.text('Tilbud', MARGIN, y)
  doc.setFont('helvetica', 'normal').setFontSize(10).setTextColor(...MUTED)
  doc.text(quote.quoteNumber, MARGIN, y + 6)

  doc.setFontSize(9)
  doc.text('Dato', RIGHT - 26, y - 2, { align: 'right' })
  doc.text('Gyldig til', RIGHT - 26, y + 3, { align: 'right' })
  doc.setTextColor(...INK)
  doc.text(formatDate(quote.quoteDate), RIGHT, y - 2, { align: 'right' })
  doc.text(formatDate(quote.validUntil), RIGHT, y + 3, { align: 'right' })

  y += 14
  doc.setDrawColor(...LINE).setLineWidth(0.3)
  doc.line(MARGIN, y, RIGHT, y)
  y += 7

  /* Modtager og afsender */
  doc.setFont('helvetica', 'normal').setFontSize(8).setTextColor(...MUTED)
  doc.text('TIL', MARGIN, y)
  doc.text('FRA', RIGHT, y, { align: 'right' })

  const toLines = [
    quote.customerName || '—',
    quote.customerAddress,
    quote.customerPhone,
    quote.customerEmail,
  ].filter(Boolean)
  const fromLines = [
    settings.companyName || 'LYKKEVÆRK',
    `CVR: ${settings.cvr}`,
    settings.phone,
    CONTACT_EMAIL,
  ].filter(Boolean)

  doc.setFontSize(10)
  toLines.forEach((line, index) => {
    doc.setFont('helvetica', index === 0 ? 'bold' : 'normal')
    doc.setTextColor(...(index === 0 ? INK : MUTED))
    doc.text(line, MARGIN, y + 6 + index * 4.6)
  })
  fromLines.forEach((line, index) => {
    doc.setFont('helvetica', index === 0 ? 'bold' : 'normal')
    doc.setTextColor(...(index === 0 ? INK : MUTED))
    doc.text(line, RIGHT, y + 6 + index * 4.6, { align: 'right' })
  })

  y += 6 + Math.max(toLines.length, fromLines.length) * 4.6 + 5
  doc.setDrawColor(...LINE)
  doc.line(MARGIN, y, RIGHT, y)
  y += 10

  /* Opgave */
  doc.setFont('helvetica', 'bold').setFontSize(14).setTextColor(...INK)
  doc.text(quote.title || 'Opgave', MARGIN, y)
  y += 6

  if (quote.description) {
    doc.setFont('helvetica', 'normal').setFontSize(10).setTextColor(...MUTED)
    const wrapped = doc.splitTextToSize(quote.description, RIGHT - MARGIN)
    doc.text(wrapped, MARGIN, y + 2)
    y += 2 + wrapped.length * 4.6
  }

  y += 6

  /* Tabel med tilbudslinjer */
  const colQty = MARGIN + 84
  const colUnit = MARGIN + 104
  const colPrice = MARGIN + 128
  const colTotal = RIGHT

  function tableHeader() {
    doc.setFont('helvetica', 'bold').setFontSize(8).setTextColor(...MUTED)
    doc.text('BESKRIVELSE', MARGIN, y)
    doc.text('ANTAL', colQty, y, { align: 'right' })
    doc.text('ENHED', colUnit, y, { align: 'right' })
    doc.text('ENHEDSPRIS', colPrice, y, { align: 'right' })
    doc.text('I ALT', colTotal, y, { align: 'right' })
    y += 2.5
    doc.setDrawColor(...LINE)
    doc.line(MARGIN, y, RIGHT, y)
    y += 5
  }

  tableHeader()

  doc.setFont('helvetica', 'normal').setFontSize(10)
  quote.lines.forEach((line) => {
    const wrapped = doc.splitTextToSize(line.description || '—', 78)
    const rowHeight = Math.max(wrapped.length * 4.4, 6)

    if (y + rowHeight > PAGE_H - 34) {
      doc.addPage()
      y = MARGIN
      tableHeader()
      doc.setFont('helvetica', 'normal').setFontSize(10)
    }

    doc.setTextColor(...INK)
    doc.text(wrapped, MARGIN, y)
    doc.setTextColor(...MUTED)
    doc.text(String(line.quantity ?? 0), colQty, y, { align: 'right' })
    doc.text(line.unit, colUnit, y, { align: 'right' })
    doc.text(kr(line.unitPrice), colPrice, y, { align: 'right' })
    doc.setTextColor(...INK)
    doc.text(kr(lineSubtotal(line)), colTotal, y, { align: 'right' })

    y += rowHeight + 2
    doc.setDrawColor(240, 238, 233)
    doc.line(MARGIN, y - 1, RIGHT, y - 1)
    y += 2
  })

  if (quote.lines.length === 0) {
    doc.setTextColor(...MUTED).setFontSize(10)
    doc.text('Ingen linjer på tilbuddet.', MARGIN, y)
    y += 8
  }

  /* Totaler */
  const totals = calculateTotals(quote.lines)
  if (y + 34 > PAGE_H - 30) {
    doc.addPage()
    y = MARGIN
  }
  y += 4

  const labelX = RIGHT - 52
  doc.setFont('helvetica', 'normal').setFontSize(10).setTextColor(...MUTED)
  doc.text('Subtotal ekskl. moms', labelX, y, { align: 'right' })
  doc.setTextColor(...INK)
  doc.text(kr(totals.subtotal), RIGHT, y, { align: 'right' })

  y += 5.5
  doc.setTextColor(...MUTED)
  doc.text('Moms', labelX, y, { align: 'right' })
  doc.setTextColor(...INK)
  doc.text(kr(totals.vatAmount), RIGHT, y, { align: 'right' })

  y += 4
  doc.setDrawColor(...LINE)
  doc.line(labelX - 24, y, RIGHT, y)
  y += 6.5

  doc.setFont('helvetica', 'bold').setFontSize(12).setTextColor(...INK)
  doc.text('Total inkl. moms', labelX, y, { align: 'right' })
  doc.text(kr(totals.total), RIGHT, y, { align: 'right' })

  y += 14

  /* Vilkår og bemærkninger */
  if (quote.customerNotes) {
    const wrapped = doc.splitTextToSize(quote.customerNotes, RIGHT - MARGIN - 10)
    const boxHeight = wrapped.length * 4.6 + 14

    if (y + boxHeight > PAGE_H - 26) {
      doc.addPage()
      y = MARGIN
    }

    doc.setFillColor(248, 246, 242)
    doc.rect(MARGIN, y, RIGHT - MARGIN, boxHeight, 'F')
    doc.setFillColor(...ACCENT)
    doc.rect(MARGIN, y, 1.2, boxHeight, 'F')

    doc.setFont('helvetica', 'bold').setFontSize(8).setTextColor(...MUTED)
    doc.text('VILKÅR OG BEMÆRKNINGER', MARGIN + 6, y + 6)
    doc.setFont('helvetica', 'normal').setFontSize(10).setTextColor(...INK)
    doc.text(wrapped, MARGIN + 6, y + 11.5)
  }

  /* Sidefod på alle sider */
  const pages = doc.getNumberOfPages()
  for (let page = 1; page <= pages; page += 1) {
    doc.setPage(page)
    doc.setDrawColor(...LINE).setLineWidth(0.3)
    doc.line(MARGIN, PAGE_H - 18, RIGHT, PAGE_H - 18)
    doc.setFont('helvetica', 'normal').setFontSize(8).setTextColor(...MUTED)
    doc.text(
      [settings.companyName || 'LYKKEVÆRK', `CVR: ${settings.cvr}`, settings.phone, CONTACT_EMAIL]
        .filter(Boolean)
        .join('  ·  '),
      MARGIN,
      PAGE_H - 12,
    )
    doc.text(`Side ${page} af ${pages}`, RIGHT, PAGE_H - 12, { align: 'right' })
  }

  return doc
}

export async function downloadQuotePdf(quote: Quote, settings: AdminSettings): Promise<void> {
  const doc = await buildQuotePdf(quote, settings)
  doc.save(quoteFileName(quote.quoteNumber))
}

/** Data-URL til forhåndsvisning i en indbygget PDF-visning. */
export async function quotePdfDataUrl(quote: Quote, settings: AdminSettings): Promise<string> {
  const doc = await buildQuotePdf(quote, settings)
  return doc.output('datauristring')
}

/** Base64 uden prefix — sendes til backend som vedhæftning. */
export async function quotePdfBase64(quote: Quote, settings: AdminSettings): Promise<string> {
  const doc = await buildQuotePdf(quote, settings)
  const dataUrl = doc.output('datauristring')
  return dataUrl.slice(dataUrl.indexOf(',') + 1)
}
